import { GraphQLClient } from 'graphql-request';
import { environment } from '../environment/environment';

// GraphQL endpoint - automatically uses the correct environment
const GRAPHQL_ENDPOINT = environment.graphqlEndpoint;

/**
 * Handle JWT expiration errors by clearing auth and redirecting to login
 */
const handleJWTExpiration = (): void => {
  // Clear localStorage auth tokens
  localStorage.removeItem('access_token');
  localStorage.removeItem('token_type');
  localStorage.removeItem('auth-storage');

  // Clear Zustand auth store
  try {
    import('../stores/authStore').then(({ default: useAuthStore }) => {
      useAuthStore.setState({
        token: null,
        user: null,
        isAuthenticated: false
      });
    });
  } catch (error) {
    console.error('Error clearing auth store:', error);
  }

  // Redirect to login page
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
};

/**
 * Check if response contains JWT expiration error
 */
const hasJWTExpirationError = (data: any): boolean => {
  if (!data || !data.errors) return false;

  const errors = Array.isArray(data.errors) ? data.errors : [data.errors];

  return errors.some((error: any) => {
    // Check for JWTExpired message
    if (error.message?.includes('JWTExpired')) return true;

    // Check in extensions
    if (error.extensions?.code === 'invalid-jwt') return true;

    // Check for variations of expired/invalid JWT in message
    const message = error.message?.toLowerCase() || '';
    if (message.includes('jwt') && (message.includes('expired') || message.includes('invalid'))) {
      return true;
    }

    return false;
  });
};

// Create GraphQL client with default headers
// NOTE: Admin secret should NEVER be used in frontend code
// Login endpoints (sendOtp, verifyOtp) should be configured as public in Hasura
const graphqlClient = new GraphQLClient(GRAPHQL_ENDPOINT, {
  headers: {
    'content-type': 'application/json',
  },
  // Custom fetch to handle JWT expiration and other errors
  fetch: async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      // Create headers object, handling both object and Headers instances
      const headers = new Headers(init?.headers || {});

      // Only add token if Authorization header is not already set
      if (!headers.has('Authorization')) {
        const token = localStorage.getItem('access_token');
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      }

      // Ensure Content-Type header is always set
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }

      // Create new init object with updated headers
      const newInit: RequestInit = {
        ...init,
        headers: headers
      };

      const response = await fetch(input, newInit);

      // Clone response to read body without consuming it
      const clonedResponse = response.clone();
      let data = null;

      try {
        data = await clonedResponse.json();
      } catch (parseError) {
        // Response body is not JSON, continue without parsing
      }

      // Check for JWT expiration errors
      if (data && hasJWTExpirationError(data)) {
        handleJWTExpiration();
        // Return error response to be handled by caller
        return response;
      }

      return response;
    } catch (err) {
      throw err;
    }
  }
});

/**
 * Decode JWT token and extract user info from claims
 */
export const decodeJWTToken = (token: string): any => {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));

    return {
      id: decoded['https://hasura.io/jwt/claims']?.['x-hasura-user-id'],
      email: decoded.email,
      name: decoded.email?.split('@')[0], // Extract name from email
      role: decoded['https://hasura.io/jwt/claims']?.['x-hasura-default-role'],
      ...decoded
    };
  } catch (error: any) {
    return null;
  }
};

// Initialize JWT token from localStorage if it exists (on app reload)
export const initializeAuthToken = (): void => {
  const token = localStorage.getItem('access_token');
  if (token) {
    setAuthToken(token);

    // Also restore user data from JWT token
    const userData = decodeJWTToken(token);
    if (userData) {
      // Import here to avoid circular dependencies
      import('../stores/authStore').then(({ default: useAuthStore }) => {
        useAuthStore.setState({ user: userData, token: token, isAuthenticated: true });
      });
    }
  } else {
    // No token in localStorage, ensure Zustand store is also cleared
    import('../stores/authStore').then(({ default: useAuthStore }) => {
      useAuthStore.setState({ token: null, user: null, isAuthenticated: false });
    });
  }
};

// Function to update client headers (useful for adding auth tokens)
export const setAuthToken = (token: string): void => {
  graphqlClient.setHeader('Authorization', `Bearer ${token}`);
};

// Function to set custom headers
export const setCustomHeaders = (headers: Record<string, string>): void => {
  graphqlClient.setHeaders(headers);
};

export default graphqlClient;
